import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import { bizuApi } from '../../api/bizu.api';
import { getBizuImageUrl } from '../../utils/images';
import { ArrowLeftIcon, UserIcon, StarIcon, CheckCircleIcon, PlayIcon, BookOpenIcon, TrophyIcon, SparklesIcon, HeartIcon, SunIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

// Step mappings for each Bizu game
const stepMappings = {
  babo: {
    1: 'Park',
    2: 'Santa Ana',
    3: 'Bizumia'
  },
  beez: {
    1: 'Intro',
    2: 'Level 1 - Small',
    3: 'Level 1 - Small',
    4: 'Level 1 - Small',
    5: 'Level 2 - Big',
    6: 'Level 2 - Big',
    7: 'Level 3 - Both',
    8: 'Level 3 - Both',
    9: 'End'
  },
  queso: {
    1: 'Intro',
    2: 'Pick Product',
    3: 'Look',
    4: 'Sniff',
    5: 'Feel',
    6: 'Touch',
    7: 'Library',
    8: 'End'
  },
  kien: {
    1: 'Intro',
    2: 'Level 1 - Tutorial',
    3: 'Level 1 - Tutorial',
    4: 'Level 2 - BBQ',
    5: 'Level 2 - BBQ',
    6: 'Level 3 - Party',
    7: 'Level 3 - Party',
    8: 'New Recipe?',
    9: 'New Recipe',
    10: 'Ingredients',
    11: 'How to Cook',
    12: 'Recipe Display',
    13: 'End'
  },
  des: {
    1: 'Bus Intro',
    2: 'Cave Intro',
    3: 'Level 1 - 1 Bead',
    4: 'Level 1 - 1 Bead',
    5: 'Level 1 - 1 Bead',
    6: 'Level 1 - 1 Bead',
    7: 'Level 2 - 5 Beads',
    8: 'Level 2 - 5 Beads',
    9: 'Level 2 - 5 Beads',
    10: 'Level 2 - 5 Beads',
    11: 'Level 2 - 5 Beads',
    12: 'Level 3 - Both Beads',
    13: 'Level 3 - Both Beads',
    14: 'Level 3 - Both Beads',
    15: 'Level 3 - Both Beads',
    16: 'Level 3 - Both Beads',
    17: 'End'
  }
};

const getStepLabel = (bizuName, step) => {
  if (!step || step === 0) return '-';
  const mapping = stepMappings[bizuName?.toLowerCase()];
  if (!mapping) return `Step ${step}`;
  return mapping[step] || `Step ${step}`;
};

export default function BizuDetailPage() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const response = await bizuApi.getStats(name);
        setStats(response.data);
      } catch (err) {
        console.error('Error fetching bizu stats:', err);
        setError('Failed to load bizu statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [name]);

  const renderStars = (count) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3].map((star) => (
          star <= count ? (
            <StarIconSolid key={star} className="h-4 w-4 text-yellow-400" />
          ) : (
            <StarIcon key={star} className="h-4 w-4 text-gray-300" />
          )
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !stats) {
    return (
      <DashboardLayout>
        <div className="text-center py-8 text-gray-500">
          {error || 'Bizu not found'}
        </div>
      </DashboardLayout>
    );
  }

  const { bizu, totalPlays, completedPlays, inProgressPlays, players } = stats;
  const isBabo = name.toLowerCase() === 'babo';
  const isQueso = name.toLowerCase() === 'queso';
  const isKien = name.toLowerCase() === 'kien';

  return (
    <DashboardLayout>
      <div className="mb-6">
        <Button
          variant="secondary"
          onClick={() => navigate('/bizu')}
          className="mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Bizus
        </Button>
      </div>

      {/* Bizu Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center gap-6">
          <div className="h-24 w-24 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
            <img
              src={getBizuImageUrl(bizu.name)}
              alt={bizu.displayName}
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-gray-900">{bizu.displayName}</h1>
              <div
                className="w-6 h-6 rounded-full border-2"
                style={{ backgroundColor: bizu.color }}
              />
            </div>
            <p className="text-gray-500">{bizu.displayName_es}</p>
            <Badge variant={bizu.isActive ? 'success' : 'secondary'} className="mt-2">
              {bizu.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-blue-100">
              <PlayIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{isBabo ? 'Total Games Started' : 'Total Players'}</p>
              <p className="text-2xl font-bold text-gray-900">{totalPlays}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{isBabo ? 'Games Completed' : 'Completed'}</p>
              <p className="text-2xl font-bold text-gray-900">{completedPlays}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-yellow-100">
              <UserIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">In Progress</p>
              <p className="text-2xl font-bold text-gray-900">{inProgressPlays}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Kien-specific Stats */}
      {isKien && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-purple-100">
                <TrophyIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">Most Played Recipe</p>
                {stats.mostPlayedRecipe ? (
                  <>
                    <p className="text-lg font-bold text-gray-900 truncate">{stats.mostPlayedRecipe.name}</p>
                    <p className="text-sm text-gray-500">{stats.mostPlayedRecipe.count} plays</p>
                  </>
                ) : (
                  <p className="text-lg text-gray-400">No data yet</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-indigo-100">
                <SparklesIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">Most Generated Recipe</p>
                {stats.mostGeneratedRecipe ? (
                  <>
                    <p className="text-lg font-bold text-gray-900 truncate">{stats.mostGeneratedRecipe.name}</p>
                    <p className="text-sm text-gray-500">{stats.mostGeneratedRecipe.count} times generated</p>
                  </>
                ) : (
                  <p className="text-lg text-gray-400">No AI recipes yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Queso-specific Stats */}
      {isQueso && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-red-100">
                <HeartIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">Most Completed Fruit</p>
                {stats.mostCompletedFruit ? (
                  <>
                    <p className="text-lg font-bold text-gray-900 truncate">{stats.mostCompletedFruit.name}</p>
                    <p className="text-sm text-gray-500">{stats.mostCompletedFruit.count} completions</p>
                  </>
                ) : (
                  <p className="text-lg text-gray-400">No data yet</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-green-100">
                <SunIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">Most Completed Veggie</p>
                {stats.mostCompletedVeggie ? (
                  <>
                    <p className="text-lg font-bold text-gray-900 truncate">{stats.mostCompletedVeggie.name}</p>
                    <p className="text-sm text-gray-500">{stats.mostCompletedVeggie.count} completions</p>
                  </>
                ) : (
                  <p className="text-lg text-gray-400">No data yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Players List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Players ({players.length})
          </h2>
        </div>

        {players.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No players have played with {bizu.displayName} yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Currently in Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Current Game Stars
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Current Stage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {isBabo ? 'Times Completed' : 'Times Played'}
                  </th>
                  {isQueso && (
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Library
                    </th>
                  )}
                  {isKien && (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Played Recipes
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        User Recipes
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {players.map((player) => (
                  <tr key={player.user_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{player.fullName}</p>
                        <p className="text-sm text-gray-500">@{player.username}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={player.role === 'adult' ? 'info' : 'secondary'}>
                        {player.role || 'member'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={player.completed ? 'success' : 'warning'}>
                        {player.completed ? 'Completed' : 'In Progress'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={player.currentlyInProgress ? 'info' : 'secondary'}>
                        {player.currentlyInProgress ? 'Yes' : 'No'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderStars(player.completed ? 3 : (isBabo ? player.step : player.stars))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getStepLabel(name, player.step)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {isBabo ? player.timesCompleted : player.timesPlayed}
                    </td>
                    {isQueso && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/bizu/queso/library/${player.user_id}`)}
                          className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 text-sm font-medium"
                        >
                          <BookOpenIcon className="h-4 w-4" />
                          View
                        </button>
                      </td>
                    )}
                    {isKien && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => navigate(`/bizu/kien/played-recipes/${player.user_id}`)}
                            className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 text-sm font-medium"
                          >
                            <BookOpenIcon className="h-4 w-4" />
                            View
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => navigate(`/bizu/kien/user-recipes/${player.user_id}`)}
                            className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 text-sm font-medium"
                          >
                            <BookOpenIcon className="h-4 w-4" />
                            View
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
